import React, { useEffect, useRef, useState } from "react";
import api, { formatApiError, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { FileArrowUp, FilePdf, FileImage, File as FileIcon, Trash, DownloadSimple } from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "birth_certificate", label: "Birth Certificate" },
  { key: "medical", label: "Medical Form" },
  { key: "registration", label: "Registration" },
  { key: "passport_photo", label: "Passport Photo" },
  { key: "insurance", label: "Insurance" },
  { key: "other", label: "Other" },
];

const iconFor = (ct) => {
  if (!ct) return FileIcon;
  if (ct.startsWith("image/")) return FileImage;
  if (ct === "application/pdf") return FilePdf;
  return FileIcon;
};

export default function DocumentsTab({ playerId }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("other");
  const inputRef = useRef(null);

  async function reload() {
    const r = await api.get(`/documents/player/${playerId}`);
    setDocs(r.data);
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [playerId]);

  async function onFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File too large (max 15MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/documents/upload?player_id=${playerId}&doc_type=${docType}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded");
      if (inputRef.current) inputRef.current.value = "";
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${id}`);
    reload();
  }

  function open(doc) {
    const token = localStorage.getItem("wahdat_token");
    const url = `${API_BASE}/documents/${doc.id}/download?auth=${encodeURIComponent(token)}`;
    window.open(url, "_blank");
  }

  return (
    <div className="space-y-4">
      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <select
          value={docType}
          onChange={(e) => setDocType(e.target.value)}
          className="h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
          data-testid="doc-type-select"
        >
          {DOC_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <input ref={inputRef} type="file" onChange={onFile} className="hidden" accept="image/*,application/pdf,.doc,.docx" data-testid="doc-file-input" />
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="doc-upload-btn"
          className="h-12 flex-1 px-6 rounded-md bg-[#D90429] hover:bg-[#b8031f] text-white font-display font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-60"
        >
          <FileArrowUp size={18} weight="bold" /> {uploading ? "Uploading…" : "Upload File"}
        </button>
      </div>

      {docs.length === 0 ? (
        <div className="text-neutral-500 text-sm p-6 text-center bg-[#121212] border border-neutral-800 rounded-lg">No documents uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {docs.map((d) => {
            const Icon = iconFor(d.content_type);
            return (
              <div key={d.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-md bg-[#0a0a0a] border border-neutral-800 flex items-center justify-center shrink-0">
                  <Icon size={22} weight="bold" color="#D90429" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{d.original_filename}</div>
                  <div className="overline mt-1">{DOC_TYPES.find((t) => t.key === d.doc_type)?.label || d.doc_type} • {(d.size / 1024).toFixed(0)} KB</div>
                </div>
                <button onClick={() => open(d)} data-testid={`doc-open-${d.id}`} className="p-2 rounded-md border border-neutral-800 hover:border-[#06D6A0] hover:text-[#06D6A0]">
                  <DownloadSimple size={16} weight="bold" />
                </button>
                <button onClick={() => remove(d.id)} data-testid={`doc-delete-${d.id}`} className="p-2 rounded-md border border-neutral-800 hover:border-[#D90429] hover:text-[#D90429]">
                  <Trash size={16} weight="bold" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
